import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(advancedFormat);

const DATE_PART_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

export interface RelativeDayLabel {
  dayKey: string;
  label: string;
}

function getDatePart(timestamp: string): string | null {
  const matched = DATE_PART_PATTERN.exec(timestamp);

  return matched?.[1] ?? null;
}

function getLocalDayKey(date: dayjs.Dayjs): string {
  return date.format('YYYY-MM-DD');
}

function formatDayKeyAsEnglishDate(dayKey: string): string {
  const date = new Date(`${dayKey}T00:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return dayKey;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

function formatMessageGroupDate(timestamp: string): string {
  return dayjs(timestamp).format('dddd, MMM Do, YYYY');
}

export function getRelativeDayLabel(
  timestamp: string,
  now: Date = new Date(),
): RelativeDayLabel | null {
  const messageDate = dayjs(timestamp);

  if (!messageDate.isValid()) {
    return null;
  }

  const nowDate = dayjs(now);
  const dayKey = getLocalDayKey(messageDate);

  if (messageDate.isSame(nowDate, 'day')) {
    return { dayKey, label: 'TODAY' };
  }

  if (messageDate.isSame(nowDate.subtract(1, 'day'), 'day')) {
    return { dayKey, label: 'YESTERDAY' };
  }

  const fallbackLabel = formatMessageGroupDate(timestamp);
  const extractedDayKey = getDatePart(timestamp);

  return {
    dayKey,
    label:
      fallbackLabel === timestamp
        ? formatDayKeyAsEnglishDate(extractedDayKey ?? dayKey)
        : fallbackLabel,
  };
}
